import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Sun, Moon, Menu, X, Briefcase, LogOut, RefreshCw, User, Zap, ChevronDown } from 'lucide-react';
import api from '../api/axios';
import UpgradeModal from './UpgradeModal';

const PageHeader = ({ onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // ── State ────────────────────────────────────────────────────────────────
  const [userData, setUserData]             = useState({ full_name: '', email: '', organization_name: '' });
  const [remaining, setRemaining]           = useState(null); // interviews_remaining
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen]   = useState(false);
  const [isRefreshing, setIsRefreshing]     = useState(false);
  const [showPlanPicker, setShowPlanPicker] = useState(false);
  const [upgradeModal, setUpgradeModal]     = useState(null); // 'growth' | 'scale' | null

  const profileRef = useRef(null);

  // ── Theme ────────────────────────────────────────────────────────────────
  const [isDark, setIsDark] = useState(() => {
    return localStorage.getItem('theme') === 'dark' || !localStorage.getItem('theme');
  });

  useEffect(() => {
    const root = document.documentElement;
    if (isDark) { root.classList.add('dark'); localStorage.setItem('theme', 'dark'); }
    else { root.classList.remove('dark'); localStorage.setItem('theme', 'light'); }
  }, [isDark]);

  // ── Fetch user + quota ───────────────────────────────────────────────────
  const fetchUserData = async () => {
    try {
      const [userRes, quotaRes] = await Promise.all([
        api.get('/users/me'),
        api.get('/me/quota'),
      ]);
      setUserData(userRes.data);
      setRemaining(quotaRes.data.remaining);
    } catch (error) { console.error('Failed to fetch user details', error); }
  };

  useEffect(() => { fetchUserData(); }, []);

  // ── Global refresh listener ──────────────────────────────────────────────
  useEffect(() => {
    const handler = () => fetchUserData();
    window.addEventListener('app-refresh', handler);
    return () => window.removeEventListener('app-refresh', handler);
  }, []);

  // ── Auto-open checkout modal if redirected from landing page ─────────────
  useEffect(() => {
    const plan = searchParams.get('checkout');
    if (plan === 'growth' || plan === 'scale') {
      setUpgradeModal(plan);
      setSearchParams({});
    }
  }, [searchParams]);

  // ── Close dropdown on outside click ─────────────────────────────────────
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setIsProfileOpen(false);
        setShowPlanPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ── Helpers ──────────────────────────────────────────────────────────────
  const getInitials = (name) => {
    if (!name) return 'U';
    const parts = name.trim().split(' ');
    if (parts.length > 1) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return name.substring(0, 2).toUpperCase();
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    window.dispatchEvent(new Event('app-refresh'));
    setTimeout(() => setIsRefreshing(false), 1000);
  };

  const closeDropdown = () => {
    setIsProfileOpen(false);
    setShowPlanPicker(false);
  };

  const isLow = remaining !== null && remaining <= 3;

  // ────────────────────────────────────────────────────────────────────────
  return (
    <>
      <header className="h-20 bg-white/80 dark:bg-[#0a0a0a]/80 border-b border-slate-200 dark:border-white/10 flex items-center justify-between px-6 lg:px-12 sticky top-0 z-40 transition-colors duration-500 backdrop-blur-md">

        {/* Brand */}
        <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/dashboard')}>
          <div className="w-8 h-8 flex items-center justify-center border border-slate-900 dark:border-white rounded-full">
            <Briefcase size={14} className="text-slate-900 dark:text-white" strokeWidth={2} />
          </div>
          <span className="font-bold text-lg tracking-[0.1em] text-slate-900 dark:text-white hidden sm:block">
            RecruitGuru
          </span>
        </div>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          {[
            { label: 'Dashboard',     path: '/dashboard' },
            { label: 'JD Repository', path: '/jd' },
            { label: 'Schedule',      path: '/schedule' },
            { label: 'Results',       path: '/results' },
            { label: 'Vendor Sourcing', path: '/vendor-sourcing' },
          ].map((item) => {
            const isActive = location.pathname.startsWith(item.path);
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`text-sm tracking-wide font-medium transition-all duration-300 ${
                  isActive
                    ? 'text-indigo-600 dark:text-indigo-400 border-b border-indigo-600 dark:border-indigo-400 pb-1'
                    : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white pb-1 border-b border-transparent'
                }`}
              >
                {item.label}
              </button>
            );
          })}
        </nav>

        {/* Right side */}
        <div className="flex items-center gap-4 lg:gap-6">

          {/* Refresh */}
          <button
            onClick={handleRefresh}
            className={`text-slate-500 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors ${isRefreshing ? 'animate-spin text-indigo-600 dark:text-indigo-400' : ''}`}
            title="Refresh Data"
          >
            <RefreshCw size={16} strokeWidth={2} />
          </button>

          {/* Theme toggle */}
          <button
            onClick={() => setIsDark(!isDark)}
            className={`relative inline-flex h-6 w-12 items-center rounded-full transition-colors duration-300 focus:outline-none ${
              isDark ? 'bg-indigo-500/20 border border-indigo-500/30' : 'bg-slate-200 border border-slate-300'
            }`}
            title="Toggle Theme"
          >
            <span className={`inline-flex h-4 w-4 transform items-center justify-center rounded-full transition-transform duration-300 ${
              isDark ? 'translate-x-7 bg-indigo-400' : 'translate-x-1 bg-white shadow-sm'
            }`}>
              {isDark ? <Moon size={10} className="text-[#0a0a0a]" /> : <Sun size={10} className="text-slate-400" />}
            </span>
          </button>

          {/* Profile + dropdown */}
          <div className="relative" ref={profileRef}>
            <div
              onClick={() => { setIsProfileOpen(!isProfileOpen); setShowPlanPicker(false); }}
              className="flex items-center gap-3 pl-2 lg:pl-4 border-l border-slate-200 dark:border-white/10 cursor-pointer group"
            >
              <div className="text-right hidden sm:block">
                <div className="text-xs font-semibold text-slate-900 dark:text-white tracking-wide">
                  {userData.full_name || 'Loading'}
                </div>
                <div className="text-[12px] text-slate-500 dark:text-slate-400 tracking-wide">
                  {userData.organization_name}
                </div>
              </div>

              {/* Avatar — amber dot when low */}
              <div className="relative">
                <div className="w-8 h-8 rounded-full border border-slate-300 dark:border-white/20 flex items-center justify-center text-xs font-light text-slate-900 dark:text-white group-hover:border-slate-900 dark:group-hover:border-white transition-colors">
                  {getInitials(userData.full_name)}
                </div>
                {isLow && (
                  <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-amber-500 rounded-full border-2 border-white dark:border-[#0a0a0a]" />
                )}
              </div>
            </div>

            {/* ── Dropdown ─────────────────────────────────────────────── */}
            {isProfileOpen && (
              <div className="absolute right-0 mt-4 w-64 bg-white dark:bg-[#111] border border-slate-200 dark:border-white/10 rounded-xl shadow-lg overflow-hidden animate-in slide-in-from-top-2 fade-in duration-200 z-50">

                {/* Mobile-only name/email */}
                <div className="px-4 py-3 border-b border-slate-100 dark:border-white/5 sm:hidden">
                  <div className="text-xs font-bold text-slate-900 dark:text-white">{userData.full_name}</div>
                  <div className="text-[12px] text-slate-500">{userData.email}</div>
                </div>

                {/* ── Interviews remaining ────────────────────────────── */}
                {remaining !== null && (
                  <div className="px-4 py-4 border-b border-slate-100 dark:border-white/5">
                    <div className="flex justify-between items-center">
                      <span className="text-[12px] tracking-wide font-bold text-slate-400">
                        Interviews Remaining
                      </span>
                      <span className={`text-sm font-bold ${isLow ? 'text-amber-500' : 'text-emerald-600 dark:text-emerald-400'}`}>
                        {remaining}
                      </span>
                    </div>
                    {isLow && (
                      <p className="text-[12px] text-amber-500 mt-1">
                        Running low — top up to keep scheduling
                      </p>
                    )}
                  </div>
                )}

                {/* ── Buy more interviews ─────────────────────────────── */}
                <div className="p-2 border-b border-slate-100 dark:border-white/5">
                  <button
                    onClick={() => setShowPlanPicker(!showPlanPicker)}
                    className="w-full flex items-center justify-between gap-3 px-3 py-2.5 text-sm font-semibold text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 rounded-lg transition-colors"
                  >
                    <span className="flex items-center gap-2">
                      <Zap size={15} strokeWidth={2} />
                      Buy Interviews
                    </span>
                    <ChevronDown
                      size={14}
                      className={`transition-transform duration-200 ${showPlanPicker ? 'rotate-180' : ''}`}
                    />
                  </button>

                  {/* Plan picker */}
                  {showPlanPicker && (
                    <div className="mt-2 space-y-2 px-1 pb-1 animate-in slide-in-from-top-1 fade-in duration-150">

                      {/* +30 interviews */}
                      <button
                        onClick={() => { setUpgradeModal('growth'); closeDropdown(); }}
                        className="w-full text-left px-3 py-3 rounded-xl border border-slate-200 dark:border-white/10 hover:border-indigo-400 dark:hover:border-indigo-500/50 hover:bg-indigo-50 dark:hover:bg-indigo-500/5 transition-all"
                      >
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="text-xs font-bold text-slate-900 dark:text-white">+30 Interviews</span>
                          <span className="text-[12px] font-bold text-indigo-600 dark:text-indigo-400">₹4,500</span>
                        </div>
                        <p className="text-[12px] text-slate-400 tracking-wide">₹150 / interview</p>
                      </button>

                      {/* +60 interviews */}
                      <button
                        onClick={() => { setUpgradeModal('scale'); closeDropdown(); }}
                        className="w-full text-left px-3 py-3 rounded-xl border border-slate-200 dark:border-white/10 hover:border-indigo-400 dark:hover:border-indigo-500/50 hover:bg-indigo-50 dark:hover:bg-indigo-500/5 transition-all"
                      >
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="text-xs font-bold text-slate-900 dark:text-white flex items-center gap-2">
                            +60 Interviews
                            <span className="text-[12px] font-bold text-emerald-500 tracking-wide">Best Value</span>
                          </span>
                          <span className="text-[12px] font-bold text-indigo-600 dark:text-indigo-400">₹7,200</span>
                        </div>
                        <p className="text-[12px] text-slate-400 tracking-wide">₹120 / interview</p>
                      </button>

                    </div>
                  )}
                </div>

                {/* Profile link */}
                <div className="p-2 border-b border-slate-100 dark:border-white/5">
                  <button
                    onClick={() => { navigate('/profile'); closeDropdown(); }}
                    className="w-full flex items-center gap-3 px-3 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5 rounded-lg transition-colors"
                  >
                    <User size={16} strokeWidth={2} />
                    My Profile
                  </button>
                </div>

                {/* Sign out */}
                <div className="p-2">
                  <button
                    onClick={onLogout}
                    className="w-full flex items-center gap-3 px-3 py-2 text-sm font-medium text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-500/10 rounded-lg transition-colors"
                  >
                    <LogOut size={16} strokeWidth={2} />
                    Sign Out
                  </button>
                </div>

              </div>
            )}
          </div>

          {/* Mobile menu toggle */}
          <button
            className="md:hidden text-slate-900 dark:text-white"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={24} strokeWidth={1} /> : <Menu size={24} strokeWidth={1} />}
          </button>
        </div>

        {/* Mobile nav dropdown */}
        {isMobileMenuOpen && (
          <div className="absolute top-20 left-0 w-full bg-white dark:bg-[#0a0a0a] border-b border-slate-200 dark:border-white/10 flex flex-col items-center py-6 gap-6 md:hidden shadow-xl z-50">
            {[
              { label: 'Dashboard',     path: '/dashboard' },
              { label: 'JD Repository', path: '/jd' },
              { label: 'Schedule',      path: '/schedule' },
              { label: 'Results',       path: '/results' },
              { label: 'Vendor Sourcing', path: '/vendor-sourcing' },
            ].map((item) => (
              <button
                key={item.path}
                onClick={() => { navigate(item.path); setIsMobileMenuOpen(false); }}
                className="text-sm tracking-wide font-medium text-slate-900 dark:text-white"
              >
                {item.label}
              </button>
            ))}

            {/* Mobile buy shortcut */}
            <button
              onClick={() => { setUpgradeModal('growth'); setIsMobileMenuOpen(false); }}
              className="flex items-center gap-2 text-sm font-bold tracking-wide text-indigo-600 dark:text-indigo-400"
            >
              <Zap size={14} /> Buy Interviews
            </button>
          </div>
        )}
      </header>

      {/* Upgrade Modal */}
      {upgradeModal && (
        <UpgradeModal
          plan={upgradeModal}
          onClose={() => setUpgradeModal(null)}
          onSuccess={() => {
            setUpgradeModal(null);
            fetchUserData();
            window.dispatchEvent(new Event('app-refresh'));
          }}
        />
      )}
    </>
  );
};

export default PageHeader;